import { useState, useCallback, useEffect, useRef } from "react";
import { getFriendlyErrorMessage } from "../utils/errorMapper";
import { io, Socket } from "socket.io-client";

const API_BASE = import.meta.env.VITE_WPP_API_BASE as string;
const SOCKET_URL = API_BASE.replace(/\/wpp\/api\/?$/, "");
const STORAGE_TOKEN_KEY = "wpp_session_state";

interface WppChatId {
  server: string;
  user: string;
  _serialized: string;
}

interface WppContact {
  id: WppChatId;
  name?: string;
  shortName?: string;
  pushname?: string;
  formattedName?: string;
  type?: string;
  isMe?: boolean;
  isMyContact?: boolean;
  isBusiness?: boolean;
  profilePicThumbObj?: {
    eurl?: string | null;
    img?: string | null;
    imgFull?: string | null;
    tag?: string;
  } | null;
}

interface WppChatPreview {
  type?: string;
  msgKey?: string;
  parentMsgKey?: string;
  reactionText?: string;
  body?: string;
  sender?: string;
  timestamp?: number;
}

export interface WppChat {
  id: WppChatId;
  t: number;
  unreadCount: number;
  archive: boolean;
  pin: number;
  isGroup: boolean;
  isUser: boolean;
  isReadOnly: boolean;
  muteExpiration: number;
  lastReceivedKey?: {
    fromMe: boolean;
    id: string;
    _serialized: string;
  };
  chatlistPreview?: WppChatPreview | null;
  contact: WppContact;
  groupMetadata?: {
    subject?: string;
    participants?: unknown[];
  } | null;
  labels?: any[];
}

export interface WppMessage {
  id: string;
  body: string;
  type: string;
  fromMe: boolean;
  t: number;
  timestamp: number;
  notifyName?: string;
  sender?: WppContact;
  chatId?: WppChatId;
  hasReaction?: boolean;
  isForwarded?: boolean;
  isGroupMsg?: boolean;
  ack?: number;
  mediaData?: Record<string, unknown>;
  caption?: string;
  quotedMsg?: {
    body: string;
    type: string;
    id?: string;
    fromMe?: boolean;
    senderName?: string;
    caption?: string;
    mediaData?: Record<string, unknown>;
  } | null;
  quotedMsgId?: string;
  reactionText?: string;
}

export type ChatFilter = "all" | "unread" | "groups";

// New interfaces for message operations
export interface LocationData {
  lat: string;
  lng: string;
  title: string;
  address: string;
}

export interface ReplyContext {
  messageId: string;
  body: string;
  senderName?: string;
  type?: string;
}

function getTokenFromStorage(sessionName: string): string | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_TOKEN_KEY}_${sessionName}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.wppToken || null;
  } catch {
    return null;
  }
}

function normalizeChat(raw: Record<string, unknown>): WppChat {
  return {
    id: raw.id as WppChatId,
    t: (raw.t as number) || 0,
    unreadCount: (raw.unreadCount as number) || 0,
    archive: Boolean(raw.archive),
    pin: (raw.pin as number) || 0,
    isGroup: Boolean(raw.isGroup),
    isUser: Boolean(raw.isUser),
    isReadOnly: Boolean(raw.isReadOnly),
    muteExpiration: (raw.muteExpiration as number) || 0,
    lastReceivedKey: raw.lastReceivedKey as WppChat["lastReceivedKey"],
    chatlistPreview: (raw.chatlistPreview as WppChatPreview) || null,
    contact: raw.contact as WppContact,
    groupMetadata: (raw.groupMetadata as WppChat["groupMetadata"]) || null,
    labels: (raw.labels as any[]) || (raw.label as any[]) || (raw.contact as any)?.labels || [],
  };
}

function normalizeMessage(raw: Record<string, unknown>): WppMessage {
  try {
    if (!raw || typeof raw !== "object") {
      return {
        id: "",
        body: "",
        type: "chat",
        fromMe: false,
        t: 0,
        timestamp: 0,
      };
    }

    let data: any = raw;
    const payload = raw.response !== undefined ? raw.response : raw.result;

    if (payload !== undefined && payload !== null) {
      if (Array.isArray(payload)) {
        if (payload.length > 0 && payload[0] !== null && typeof payload[0] === "object") {
          data = payload[0];
        }
      } else if (typeof payload === "object") {
        data = payload;
      }
    } else if (Array.isArray(raw) && raw.length > 0) {
      if (raw[0] !== null && typeof raw[0] === "object") {
        data = raw[0];
      }
    }

    if (!data || typeof data !== "object") {
      data = {};
    }

    let resolvedId = "";
    if (data.id !== undefined && data.id !== null) {
      if (typeof data.id === "string") {
        resolvedId = data.id;
      } else if (typeof data.id === "object") {
        resolvedId = (((data.id as any)._serialized || (data.id as any).id || "") as string);
      }
    }

    let resolvedQuotedMsgId = "";
    if (data.quotedMsgId !== undefined && data.quotedMsgId !== null) {
      if (typeof data.quotedMsgId === "string") {
        resolvedQuotedMsgId = data.quotedMsgId;
      } else if (typeof data.quotedMsgId === "object") {
        resolvedQuotedMsgId = (((data.quotedMsgId as any)._serialized || (data.quotedMsgId as any).id || "") as string);
      }
    }

    const quotedMsgRaw = data.quotedMsg;
    let normalizedQuotedMsg = null;
    if (quotedMsgRaw && typeof quotedMsgRaw === "object") {
      let quotedId = "";
      if (quotedMsgRaw.id !== undefined && quotedMsgRaw.id !== null) {
        if (typeof quotedMsgRaw.id === "string") {
          quotedId = quotedMsgRaw.id;
        } else if (typeof quotedMsgRaw.id === "object") {
          quotedId = (((quotedMsgRaw.id as any)._serialized || (quotedMsgRaw.id as any).id || "") as string);
        }
      }

      normalizedQuotedMsg = {
        body: (quotedMsgRaw.body as string) || "",
        type: (quotedMsgRaw.type as string) || "chat",
        id: quotedId,
        fromMe: Boolean(quotedMsgRaw.fromMe),
        senderName:
          (quotedMsgRaw.notifyName as string) ||
          (quotedMsgRaw.sender as { name?: string; pushname?: string })?.pushname ||
          (quotedMsgRaw.sender as { name?: string; pushname?: string })?.name ||
          "",
        caption:
          (quotedMsgRaw.caption as string) ||
          ((quotedMsgRaw.mediaData as Record<string, unknown> | undefined)?.caption as string) ||
          "",
        mediaData: (quotedMsgRaw.mediaData as Record<string, unknown>) || undefined,
      };
    }

    return {
      id: resolvedId,
      body: (data.body as string) || "",
      type: (data.type as string) || "chat",
      fromMe: Boolean(data.fromMe),
      t: (data.t as number) || (data.timestamp as number) || 0,
      timestamp: (data.timestamp as number) || (data.t as number) || 0,
      notifyName: data.notifyName as string,
      sender: data.sender as WppContact,
      chatId: data.chatId as WppChatId,
      hasReaction: Boolean(data.hasReaction),
      isForwarded: Boolean(data.isForwarded),
      isGroupMsg: Boolean(data.isGroupMsg),
      ack: data.ack as number,
      mediaData: data.mediaData as Record<string, unknown>,
      caption: (data.caption as string) || ((data.mediaData as Record<string, unknown> | undefined)?.caption as string) || "",
      quotedMsg: normalizedQuotedMsg,
      quotedMsgId: resolvedQuotedMsgId,
      reactionText: data.reactionText as string,
    };
  } catch (err) {
    console.error("normalizeMessage failed to parse raw message object:", raw, err);
    return {
      id: "",
      body: "",
      type: "chat",
      fromMe: false,
      t: 0,
      timestamp: 0,
    };
  }
}

function getMessageChatPhone(msg: WppMessage, rawMsgData: any): string {
  // Method 1: Extract from message ID (most robust)
  if (msg.id && typeof msg.id === "string") {
    const parts = msg.id.split("_");
    if (parts.length >= 2 && parts[1].includes("@")) {
      return parts[1].split("@")[0];
    }
  }

  // Method 2: Extract from raw payload chatId (could be object or string)
  const rawChatId = rawMsgData?.chatId;
  if (rawChatId) {
    if (typeof rawChatId === "string") {
      return rawChatId.split("@")[0];
    } else if (typeof rawChatId === "object") {
      if (rawChatId.user) return rawChatId.user;
      if (rawChatId._serialized) return rawChatId._serialized.split("@")[0];
    }
  }

  // Method 3: Extract from msg.chatId if populated
  if (msg.chatId) {
    if (typeof msg.chatId === "string") {
      return (msg.chatId as string).split("@")[0];
    } else if ((msg.chatId as any).user) {
      return (msg.chatId as any).user;
    }
  }

  // Method 4: Extract from from/to fields
  const from = rawMsgData?.from;
  const to = rawMsgData?.to;
  const jid = msg.fromMe ? to : from;
  if (jid && typeof jid === "string") {
    return jid.split("@")[0];
  }

  return "";
}

export function useWhatsAppChat(sessionName: string) {
  const [chats, setChats] = useState<WppChat[]>([]);
  const [archivedChats, setArchivedChats] = useState<WppChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<WppChat | null>(null);
  const [messages, setMessages] = useState<WppMessage[]>([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [aiReplying, setAiReplying] = useState(false);
  const [error, setErrorState] = useState<string | null>(null);
  const setError = useCallback((err: string | null) => {
    setErrorState(err ? getFriendlyErrorMessage(err) : null);
  }, []);
  const [filter, setFilter] = useState<ChatFilter>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [lastMessages, setLastMessages] = useState<Record<string, { body: string; type: string; fromMe: boolean; t: number; senderName?: string }>>({});

  const socketRef = useRef<Socket | null>(null);
  const seenMsgIds = useRef<Set<string>>(new Set());
  const selectedChatRef = useRef<WppChat | null>(null);
  const fetchChatsRef = useRef<(() => void) | null>(null);
  const sendSeenRef = useRef<((phone: string, isGroup?: boolean) => Promise<boolean>) | null>(null);
  const lidCacheRef = useRef<Map<string, string>>(new Map());

  const [token, setToken] = useState<string | null>(() => getTokenFromStorage(sessionName));

  // Track if token is invalid so parent can auto-restart scan
  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    // Initial sync when sessionName changes
    setToken(getTokenFromStorage(sessionName));
    setTokenInvalid(false);

    // Poll for localStorage changes
    const interval = setInterval(() => {
      const currentToken = getTokenFromStorage(sessionName);
      if (currentToken !== token) {
        setToken(currentToken);
      }
    }, 1500);

    return () => clearInterval(interval);
  }, [sessionName, token]);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  const apiHeaders = useCallback((): Record<string, string> => {
    const t = token || getTokenFromStorage(sessionName) || "";
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${t}`,
    };
  }, [token, sessionName]);

  const resolveLidToPhone = useCallback(async (lidJid: string): Promise<string | null> => {
    if (!lidJid) return null;
    const cleanLid = lidJid.includes("@") ? lidJid : `${lidJid}@lid`;
    
    // Check in-memory Map cache
    if (lidCacheRef.current.has(cleanLid)) {
      return lidCacheRef.current.get(cleanLid) || null;
    }

    try {
      const res = await fetch(`${API_BASE}/${sessionName}/contact/pn-lid/${encodeURIComponent(cleanLid)}`, {
        headers: apiHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.phoneNumber && data.phoneNumber.id) {
          const resolvedPhone = data.phoneNumber.id;
          lidCacheRef.current.set(cleanLid, resolvedPhone);
          return resolvedPhone;
        }
      }
    } catch (err) {
      console.error("Failed to resolve LID to phone number:", err);
    }
    return null;
  }, [sessionName, apiHeaders]);

  const getTargetPhone = useCallback(async (phone: string): Promise<string> => {
    if (phone.includes("@lid")) {
      const resolved = await resolveLidToPhone(phone);
      return resolved || phone;
    }
    
    // Check if any chat in the chats list matches this user ID on the lid server
    const matchingChat = chats.find((c) => c.id.user === phone);
    if (matchingChat && matchingChat.id.server === "lid") {
      const resolved = await resolveLidToPhone(matchingChat.id._serialized);
      return resolved || phone;
    }

    if (selectedChatRef.current?.id.server === "lid" && phone === selectedChatRef.current.id.user) {
      const resolved = await resolveLidToPhone(selectedChatRef.current.id._serialized);
      return resolved || phone;
    }
    return phone;
  }, [resolveLidToPhone, chats]);

  useEffect(() => {
    if (selectedChat && selectedChat.id.server === "lid") {
      resolveLidToPhone(selectedChat.id._serialized);
    }
  }, [selectedChat, resolveLidToPhone]);


  const fetchChats = useCallback(async () => {
    if (!sessionName || !token) return;
    setLoadingChats(true);
    setError(null);

    try {
      const body: Record<string, unknown> = {};
      if (filter === "unread") body.onlyWithUnreadMessage = true;
      if (filter === "groups") body.onlyGroups = true;

      const res = await fetch(`${API_BASE}/${sessionName}/list-chats`, {
        method: "POST",
        headers: apiHeaders(),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 404) {
          setTokenInvalid(true);
          setChats([]);
          if (sessionName) {
            localStorage.removeItem(`wpp_session_state_${sessionName}`);
          }
          return;
        }
        throw new Error(`Failed to load chats (${res.status})`);
      }

      const rawJson: any = await res.json();

      // Check if server returned a disconnection error payload in HTTP 200 OK
      if (
        !rawJson ||
        rawJson.status === false ||
        rawJson.status === "NOT_CONNECTED" ||
        rawJson.status === "DISCONNECTED" ||
        rawJson.status === "CLOSED" ||
        rawJson.response === false ||
        (typeof rawJson.message === "string" && /not connected|disconnected|logged out|unauthorized/i.test(rawJson.message))
      ) {
        setTokenInvalid(true);
        setChats([]);
        if (sessionName) {
          localStorage.removeItem(`wpp_session_state_${sessionName}`);
        }
        setError("WhatsApp is disconnected. Please connect again.");
        return;
      }

      const list: any[] = Array.isArray(rawJson)
        ? rawJson
        : Array.isArray(rawJson?.response)
        ? rawJson.response
        : Array.isArray(rawJson?.result)
        ? rawJson.result
        : Array.isArray(rawJson?.data)
        ? rawJson.data
        : [];

      const normalized = list
        .filter((c) => c && typeof c === "object")
        .map(normalizeChat)
        .filter((c) => !c.archive);

      const pinned = normalized
        .filter((c) => c.pin > 0)
        .sort((a, b) => b.pin - a.pin);
      const unpinned = normalized
        .filter((c) => !c.pin)
        .sort((a, b) => b.t - a.t);

      setChats([...pinned, ...unpinned]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load chats");
    } finally {
      setLoadingChats(false);
    }
  }, [sessionName, token, filter, apiHeaders]);

  const fetchArchivedChats = useCallback(async () => {
    if (!sessionName || !token) return;
    try {
      const res = await fetch(`${API_BASE}/${sessionName}/all-chats-archived`, {
        headers: apiHeaders(),
      });

      if (!res.ok) {
        if (res.status === 404 || res.status === 401) {
          setArchivedChats([]);
          return;
        }
        throw new Error(`Failed to load archived chats (${res.status})`);
      }

      const json: unknown[] = await res.json();
      const normalized = (json as Record<string, unknown>[]).map(normalizeChat);
      setArchivedChats(normalized);
    } catch {
      // silent fail for archived
    }
  }, [sessionName, token, apiHeaders]);

  useEffect(() => {
    fetchChatsRef.current = fetchChats;
  }, [fetchChats]);

  const fetchMessages = useCallback(
    async (chatId: string, isGroup?: boolean) => {
      if (!sessionName || !token || !chatId) return;
      setLoadingMessages(true);
      setError(null);

      try {
        const resolvedIsGroup = isGroup ?? (selectedChatRef.current?.id._serialized === chatId ? selectedChatRef.current.isGroup : false);
        const queryParams = new URLSearchParams({
          includeMe: "true",
          includeNotifications: "true",
          count: "100",
          direction: "before",
        });
        if (resolvedIsGroup) {
          queryParams.append("isGroup", "true");
        }

        const res = await fetch(
          `${API_BASE}/${sessionName}/get-messages/${chatId}?${queryParams.toString()}`,
          { headers: apiHeaders() },
        );

        if (!res.ok) throw new Error(`Failed to load messages (${res.status})`);

        const json = (await res.json()) as {
          status: string;
          response: Record<string, unknown>[];
        };

        if (json.status === "success" && Array.isArray(json.response)) {
          const normalized = json.response.map((m, i) => {
            try {
              return normalizeMessage(m);
            } catch (err) {
              console.error(`[fetchMessages] Error at index ${i}:`, err);
              throw err;
            }
          });
          normalized.sort((a, b) => a.timestamp - b.timestamp);
          seenMsgIds.current = new Set(normalized.map((m) => m.id));
          setMessages(normalized);
        } else {
          setMessages([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load messages");
      } finally {
        setLoadingMessages(false);
      }
    },
    [sessionName, token, apiHeaders],
  );

  const handleSentResponse = useCallback(
    (responseText: string, tempMsg: WppMessage): WppMessage => {
      try {
        const json = JSON.parse(responseText);
        const realMsg = normalizeMessage(json);
        if (realMsg && realMsg.id && !realMsg.id.startsWith("temp_") && realMsg.id !== "") {
          seenMsgIds.current.add(realMsg.id);

          // Merge mediaData and quotedMsg from tempMsg if missing in the realMsg
          if (!realMsg.mediaData && tempMsg.mediaData) {
            realMsg.mediaData = tempMsg.mediaData;
          }
          if (!realMsg.quotedMsg && tempMsg.quotedMsg) {
            realMsg.quotedMsg = tempMsg.quotedMsg;
          }

          return realMsg;
        }
      } catch (e) {
        console.warn("Failed to parse sent message response JSON:", e);
      }
      seenMsgIds.current.add(tempMsg.id);
      return tempMsg;
    },
    []
  );

  const sendMessage = useCallback(
    async (phone: string, text: string, isGroup?: boolean) => {
      if (!sessionName || !token || !phone || !text.trim()) return false;
      setSendingMessage(true);
      setError(null);

      try {
        const targetPhone = await getTargetPhone(phone);
        const resolvedIsGroup = isGroup ?? (selectedChatRef.current?.id.user === phone ? selectedChatRef.current.isGroup : false);
        const res = await fetch(`${API_BASE}/${sessionName}/send-message`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ phone: targetPhone, isGroup: resolvedIsGroup, message: text.trim() }),
        });

        const responseText = await res.text();
        let errorMsg = `Failed to send message (${res.status})`;

        if (!res.ok) {
          try {
            const json = JSON.parse(responseText);
            errorMsg = json.message || json.error || json.response || errorMsg;
          } catch {
            if (responseText) errorMsg = responseText.slice(0, 200);
          }
          throw new Error(errorMsg);
        }

        const tempMsg: WppMessage = {
          id: `temp_${Date.now()}`,
          body: text.trim(),
          type: "chat",
          fromMe: true,
          t: Math.floor(Date.now() / 1000),
          timestamp: Math.floor(Date.now() / 1000),
          ack: 1,
        };

        const finalMsg = handleSentResponse(responseText, tempMsg);
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === finalMsg.id);
          if (exists) return prev;
          return [...prev, finalMsg];
        });
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to send message");
        return false;
      } finally {
        setSendingMessage(false);
      }
    },
    [sessionName, token, apiHeaders, getTargetPhone],
  );

  // --- send-reply ---
  const sendReply = useCallback(
    async (phone: string, messageId: string, text: string, isGroup = false) => {
      if (!sessionName || !token || !phone || !text.trim() || !messageId) return false;
      setSendingMessage(true);
      setError(null);

      try {
        const targetPhone = await getTargetPhone(phone);
        const res = await fetch(`${API_BASE}/${sessionName}/send-reply`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ phone: targetPhone, isGroup, message: text.trim(), messageId }),
        });

        const responseText = await res.text();
        let errorMsg = `Failed to send reply (${res.status})`;

        if (!res.ok) {
          try {
            const json = JSON.parse(responseText);
            errorMsg = json.message || json.error || json.response || errorMsg;
          } catch {
            if (responseText) errorMsg = responseText.slice(0, 200);
          }
          throw new Error(errorMsg);
        }

        const tempMsg: WppMessage = {
          id: `temp_${Date.now()}`,
          body: text.trim(),
          type: "chat",
          fromMe: true,
          t: Math.floor(Date.now() / 1000),
          timestamp: Math.floor(Date.now() / 1000),
          ack: 1,
          quotedMsgId: messageId,
        };

        const finalMsg = handleSentResponse(responseText, tempMsg);
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === finalMsg.id);
          if (exists) return prev;
          return [...prev, finalMsg];
        });
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to send reply");
        return false;
      } finally {
        setSendingMessage(false);
      }
    },
    [sessionName, token, apiHeaders],
  );

  // --- send-image ---
  const sendImage = useCallback(
    async (phone: string, base64: string, filename: string, caption = "", isGroup = false) => {
      if (!sessionName || !token || !phone || !base64) return false;
      setSendingMessage(true);
      setError(null);

      try {
        const targetPhone = await getTargetPhone(phone);
        const res = await fetch(`${API_BASE}/${sessionName}/send-image`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ phone: targetPhone, isGroup, isNewsletter: false, isLid: false, filename, caption, base64 }),
        });

        const responseText = await res.text();
        let errorMsg = `Failed to send image (${res.status})`;

        if (!res.ok) {
          try {
            const json = JSON.parse(responseText);
            errorMsg = json.message || json.error || json.response || errorMsg;
          } catch {
            if (responseText) errorMsg = responseText.slice(0, 200);
          }
          throw new Error(errorMsg);
        }

        const tempMsg: WppMessage = {
          id: `temp_${Date.now()}`,
          body: caption || "📷 Image",
          type: "image",
          fromMe: true,
          t: Math.floor(Date.now() / 1000),
          timestamp: Math.floor(Date.now() / 1000),
          ack: 1,
          mediaData: { type: "image", base64 },
        };

        const finalMsg = handleSentResponse(responseText, tempMsg);
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === finalMsg.id);
          if (exists) return prev;
          return [...prev, finalMsg];
        });
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to send image");
        return false;
      } finally {
        setSendingMessage(false);
      }
    },
    [sessionName, token, apiHeaders, getTargetPhone],
  );

  // --- send-voice ---
  const sendVoice = useCallback(
    async (phone: string, base64Ptt: string, isGroup = false) => {
      if (!sessionName || !token || !phone || !base64Ptt) return false;
      setSendingMessage(true);
      setError(null);

      try {
        const targetPhone = await getTargetPhone(phone);
        const res = await fetch(`${API_BASE}/${sessionName}/send-voice-base64`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ phone: targetPhone, isGroup, base64Ptt }),
        });

        const responseText = await res.text();
        let errorMsg = `Failed to send voice (${res.status})`;

        if (!res.ok) {
          try {
            const json = JSON.parse(responseText);
            errorMsg = json.message || json.error || json.response || errorMsg;
          } catch {
            if (responseText) errorMsg = responseText.slice(0, 200);
          }
          throw new Error(errorMsg);
        }

        const tempMsg: WppMessage = {
          id: `temp_${Date.now()}`,
          body: "🎤 Voice message",
          type: "ptt",
          fromMe: true,
          t: Math.floor(Date.now() / 1000),
          timestamp: Math.floor(Date.now() / 1000),
          ack: 1,
          mediaData: { type: "ptt" },
        };

        const finalMsg = handleSentResponse(responseText, tempMsg);
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === finalMsg.id);
          if (exists) return prev;
          return [...prev, finalMsg];
        });
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to send voice");
        return false;
      } finally {
        setSendingMessage(false);
      }
    },
    [sessionName, token, apiHeaders, getTargetPhone],
  );

  // --- send-file ---
  const sendFile = useCallback(
    async (phone: string, base64: string, filename: string, caption = "", isGroup = false) => {
      if (!sessionName || !token || !phone || !base64 || !filename) return false;
      setSendingMessage(true);
      setError(null);

      try {
        const targetPhone = await getTargetPhone(phone);
        const res = await fetch(`${API_BASE}/${sessionName}/send-file-base64`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ phone: targetPhone, isGroup, isNewsletter: false, isLid: false, filename, caption, base64 }),
        });

        const responseText = await res.text();
        let errorMsg = `Failed to send file (${res.status})`;

        if (!res.ok) {
          try {
            const json = JSON.parse(responseText);
            errorMsg = json.message || json.error || json.response || errorMsg;
          } catch {
            if (responseText) errorMsg = responseText.slice(0, 200);
          }
          throw new Error(errorMsg);
        }

        const tempMsg: WppMessage = {
          id: `temp_${Date.now()}`,
          body: caption || `📎 ${filename}`,
          type: "document",
          fromMe: true,
          t: Math.floor(Date.now() / 1000),
          timestamp: Math.floor(Date.now() / 1000),
          ack: 1,
          mediaData: { type: "document", filename },
        };

        const finalMsg = handleSentResponse(responseText, tempMsg);
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === finalMsg.id);
          if (exists) return prev;
          return [...prev, finalMsg];
        });
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to send file");
        return false;
      } finally {
        setSendingMessage(false);
      }
    },
    [sessionName, token, apiHeaders],
  );

  // --- send-location ---
  const sendLocation = useCallback(
    async (phone: string, location: LocationData, isGroup = false) => {
      if (!sessionName || !token || !phone) return false;
      setSendingMessage(true);
      setError(null);

      try {
        const targetPhone = await getTargetPhone(phone);
        const res = await fetch(`${API_BASE}/${sessionName}/send-location`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({
            phone: targetPhone,
            isGroup,
            lat: location.lat,
            lng: location.lng,
            title: location.title,
            address: location.address,
          }),
        });

        const responseText = await res.text();
        let errorMsg = `Failed to send location (${res.status})`;

        if (!res.ok) {
          try {
            const json = JSON.parse(responseText);
            errorMsg = json.message || json.error || json.response || errorMsg;
          } catch {
            if (responseText) errorMsg = responseText.slice(0, 200);
          }
          throw new Error(errorMsg);
        }

        const tempMsg: WppMessage = {
          id: `temp_${Date.now()}`,
          body: `📍 ${location.title}`,
          type: "location",
          fromMe: true,
          t: Math.floor(Date.now() / 1000),
          timestamp: Math.floor(Date.now() / 1000),
          ack: 1,
          mediaData: {
            type: "location",
            lat: location.lat,
            lng: location.lng,
            title: location.title,
            address: location.address,
          },
        };

        const finalMsg = handleSentResponse(responseText, tempMsg);
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === finalMsg.id);
          if (exists) return prev;
          return [...prev, finalMsg];
        });
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to send location");
        return false;
      } finally {
        setSendingMessage(false);
      }
    },
    [sessionName, token, apiHeaders, getTargetPhone],
  );

  // --- send-link ---
  const sendLink = useCallback(
    async (phone: string, url: string, caption = "", isGroup = false) => {
      if (!sessionName || !token || !phone || !url) return false;
      setSendingMessage(true);
      setError(null);

      try {
        const targetPhone = await getTargetPhone(phone);
        const res = await fetch(`${API_BASE}/${sessionName}/send-link-preview`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ phone: targetPhone, isGroup, url, caption }),
        });

        const responseText = await res.text();
        let errorMsg = `Failed to send link (${res.status})`;

        if (!res.ok) {
          try {
            const json = JSON.parse(responseText);
            errorMsg = json.message || json.error || json.response || errorMsg;
          } catch {
            if (responseText) errorMsg = responseText.slice(0, 200);
          }
          throw new Error(errorMsg);
        }

        const tempMsg: WppMessage = {
          id: `temp_${Date.now()}`,
          body: caption || url,
          type: "link",
          fromMe: true,
          t: Math.floor(Date.now() / 1000),
          timestamp: Math.floor(Date.now() / 1000),
          ack: 1,
          mediaData: { type: "link", url },
        };

        const finalMsg = handleSentResponse(responseText, tempMsg);
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === finalMsg.id);
          if (exists) return prev;
          return [...prev, finalMsg];
        });
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to send link");
        return false;
      } finally {
        setSendingMessage(false);
      }
    },
    [sessionName, token, apiHeaders, getTargetPhone],
  );

  // --- react-message ---
  const reactToMessage = useCallback(
    async (messageId: string, reaction: string) => {
      if (!sessionName || !token || !messageId) return false;
      setError(null);

      try {
        const res = await fetch(`${API_BASE}/${sessionName}/react-message`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ msgId: messageId, reaction }),
        });

        if (!res.ok) throw new Error(`Failed to react (${res.status})`);

        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? { ...m, hasReaction: true, reactionText: reaction || undefined }
              : m,
          ),
        );
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to react to message");
        return false;
      }
    },
    [sessionName, token, apiHeaders],
  );

  // --- download-media ---
  // --- send-ai-reply ---
  const sendAiReply = useCallback(
    async (msg: WppMessage): Promise<boolean> => {
      if (!sessionName || !msg || !msg.id || msg.fromMe) return false;

      setAiReplying(true);

      const authRaw = localStorage.getItem("auth_user");
      let authToken = "";
      if (authRaw) {
        try {
          const parsed = JSON.parse(authRaw);
          authToken = parsed?.token || "";
        } catch {
          // ignore
        }
      }

      if (!authToken) {
        setError("Authentication token not found. Please login again.");
        return false;
      }

      // Extract "from" from message ID: e.g. "false_201012345678@c.us_3EB0..." → "201012345678@c.us"
      let from = "";
      const idParts = msg.id.split("_");
      if (idParts.length >= 2 && idParts[1].includes("@")) {
        from = idParts[1];
      }

      // Determine type and message based on message.type
      let aiType = "chat";
      let aiMessage = "";

      switch (msg.type) {
        case "chat":
        case "text":
          aiType = "chat";
          aiMessage = msg.body || "";
          break;
        case "ptt":
        case "audio":
        case "voice":
          aiType = "ptt";
          aiMessage = "";
          break;
        case "image":
        case "photo":
          aiType = "image";
          aiMessage = msg.caption || "";
          break;
        case "video":
          aiType = "video";
          aiMessage = msg.caption || "";
          break;
        case "document":
        case "file":
          aiType = "document";
          aiMessage = msg.caption || "";
          break;
        default:
          aiType = "chat";
          aiMessage = msg.body || msg.caption || "";
          break;
      }

      const AI_API_BASE = API_BASE.replace(/\/wpp\/api\/?$/, "/ai");

      try {
        const res = await fetch(`${AI_API_BASE}/reply/${sessionName}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            from,
            type: aiType,
            id: msg.id,
            message: aiMessage,
          }),
        });

        if (!res.ok) {
          const errorText = await res.text();
          let errorMsg = `AI Reply failed (${res.status})`;
          try {
            const json = JSON.parse(errorText);
            errorMsg = json.message || json.error || errorMsg;
          } catch {
            if (errorText) errorMsg = errorText.slice(0, 200);
          }
          throw new Error(errorMsg);
        }

        const data = await res.json();
        if (data.success) {
          setAiReplying(false);
          return true;
        }
        throw new Error(data.message || "AI Reply failed");
      } catch (err) {
        setAiReplying(false);
        setError(err instanceof Error ? err.message : "AI Reply failed");
        return false;
      }
    },
    [sessionName],
  );

  const downloadMedia = useCallback(
    async (messageId: string): Promise<string | null> => {
      if (!sessionName || !token || !messageId) return null;
      setError(null);

      try {
        const res = await fetch(`${API_BASE}/${sessionName}/download-media`, {
          method: "POST",
          headers: apiHeaders(),
          body: JSON.stringify({ messageId }),
        });

        const contentType = res.headers.get("content-type") || "";

        // Read response text/clone to detect JSON
        const clone = res.clone();
        let isJson = contentType.includes("application/json");
        let json: any = null;
        let responseText = "";

        try {
          responseText = await clone.text();
          if (responseText.trim().startsWith("{")) {
            json = JSON.parse(responseText);
            isJson = true;
          }
        } catch (e) {
          // Not JSON
        }

        if (isJson && json) {
          if (json.status === "error" || json.error) {
            throw new Error(json.message || json.error || "Server returned error in download-media");
          }

          const possibleData = json.base64 || json.response || json.data || json.url || json.mediaUrl;
          let dataStr = "";
          let mime = json.mimetype || "image/jpeg";

          if (possibleData) {
            if (typeof possibleData === "string") {
              dataStr = possibleData;
            } else if (typeof possibleData === "object" && possibleData !== null) {
              const obj = possibleData as Record<string, unknown>;
              dataStr = (obj.base64 || obj.url || obj.data || "") as string;
              if (obj.mimetype) mime = obj.mimetype as string;
            }
          }

          if (dataStr) {
            if (
              dataStr.startsWith("data:") ||
              dataStr.startsWith("http://") ||
              dataStr.startsWith("https://")
            ) {
              return dataStr;
            } else if (dataStr.startsWith("/")) {
              return dataStr;
            } else {
              // Optimize base64 check by testing a small sample to avoid stack overflow on huge strings
              const sample = dataStr.slice(0, 150).replace(/\s/g, "");
              const isBase64 = /^[A-Za-z0-9+/=\/]{40,}/.test(sample);
              if (isBase64) {
                const cleanB64 = dataStr.includes(" ") || dataStr.includes("\n") ? dataStr.replace(/\s+/g, "") : dataStr;
                const mimeType = mime || "image/jpeg";
                return `data:${mimeType};base64,${cleanB64}`;
              }
            }
          }

          return null;
        }

        if (!res.ok) throw new Error(`Failed to download media (${res.status})`);

        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        return url;
      } catch (err) {
        console.error("[useWhatsAppChat] Failed to download media:", err);
        return null;
      }
    },
    [sessionName, token, apiHeaders],
  );

  const sendSeen = useCallback(
    async (phone: string, isGroup?: boolean) => {
      if (!sessionName || !token || !phone) return false;
      try {
        const targetPhone = await getTargetPhone(phone);
        const resolvedIsGroup = isGroup ?? (selectedChatRef.current?.id.user === phone ? selectedChatRef.current.isGroup : false);

        // Helper function to request send-seen endpoint
        const requestSendSeen = async (target: string) => {
          const res = await fetch(`${API_BASE}/${sessionName}/send-seen`, {
            method: "POST",
            headers: apiHeaders(),
            body: JSON.stringify({ phone: target, isGroup: resolvedIsGroup }),
          });
          return res.ok;
        };

        // Determine if we have a lid JID
        const lidJid = phone.includes("@lid")
          ? phone
          : chats.find((c) => c.id.user === phone && c.id.server === "lid")?.id._serialized ||
            (selectedChatRef.current?.id.user === phone && selectedChatRef.current.id.server === "lid"
              ? selectedChatRef.current.id._serialized
              : null);

        let success = false;

        // 1. Try with LID first
        if (lidJid) {
          try {
            success = await requestSendSeen(lidJid);
          } catch {
            // fallback to phone
          }
        }

        // 2. Try with resolved phone number as fallback
        if (!success) {
          success = await requestSendSeen(targetPhone);
        }

        if (!success) {
          console.warn("[Socket.io] Failed to send-seen status for both LID and phone");
          return false;
        }

        // Also update local state so the unread count goes to 0 immediately in the UI!
        const updateUnreadCount = (c: WppChat) => c.id.user === phone ? { ...c, unreadCount: 0 } : c;
        setChats((prevChats) => prevChats.map(updateUnreadCount));
        setArchivedChats((prevChats) => prevChats.map(updateUnreadCount));
        return true;
      } catch (err) {
        console.error("[Socket.io] Error in sendSeen:", err);
        return false;
      }
    },
    [sessionName, token, apiHeaders, getTargetPhone, chats]
  );

  useEffect(() => {
    sendSeenRef.current = sendSeen;
  }, [sendSeen]);

  const selectChat = useCallback(
    (chat: WppChat) => {
      setSelectedChat(chat);
      setError(null);
      const phone = chat.id.user;
      fetchMessages(chat.id._serialized, chat.isGroup);
      sendSeen(phone, chat.isGroup);
    },
    [fetchMessages, sendSeen],
  );

  const fetchLastMessages = useCallback(
    async (chatList: WppChat[]) => {
      if (!sessionName || !token || chatList.length === 0) return;

      const CONCURRENCY = 5;
      const queue = [...chatList];

      const worker = async () => {
        while (queue.length > 0) {
          const chat = queue.shift();
          if (!chat) break;

          try {
            const phone = chat.id._serialized;
            const res = await fetch(
              `${API_BASE}/${sessionName}/get-messages/${phone}?count=1&direction=before`,
              { headers: apiHeaders() },
            );

            if (!res.ok) continue;

            const json = await res.json() as { status: string; response: Array<Record<string, unknown>> };
            if (json.status === "success" && Array.isArray(json.response) && json.response.length > 0) {
              const msg = json.response[0];
              const sender = msg.sender as Record<string, unknown> | undefined;

              setLastMessages((prev) => ({
                ...prev,
                [chat.id._serialized]: {
                  body: (msg.body as string) || "",
                  type: (msg.type as string) || "chat",
                  fromMe: Boolean(msg.fromMe),
                  t: (msg.t as number) || (msg.timestamp as number) || 0,
                  senderName: (sender?.pushname as string) || (sender?.formattedName as string) || undefined,
                },
              }));
            }
          } catch {
            // silent fail for individual chat
          }
        }
      };

      // Launch CONCURRENCY number of workers
      const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, () => worker());
      await Promise.all(workers);
    },
    [sessionName, token, apiHeaders],
  );

  // --- Socket.io real-time connection ---
  // --- Socket.io real-time connection ---
  useEffect(() => {
    if (!sessionName || !token) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socket: Socket = io(SOCKET_URL, {
      path: "/wpp/socket.io/",
      transports: ["polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15000,
      timeout: 20000,
      auth: {
        token: token,
      },
      query: {
        token: token,
      },
      extraHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("join", sessionName);
    });

    socket.on("connect_error", (err: Error) => {
      if (
        err.message.includes("400") ||
        err.message.includes("401") ||
        err.message.includes("404") ||
        err.message.includes("Unauthorized") ||
        err.message.includes("handshake")
      ) {
        setTokenInvalid(true);
        setLoadingChats(false);
        socket.disconnect();
      }
    });

    socket.on("disconnect", (reason: string) => {
      if (reason === "io server disconnect") {
        socket.connect();
      }
    });

    const handleNewMessage = (rawMsg: Record<string, unknown>, sourceEvent: string) => {
      const msg = normalizeMessage(rawMsg);

      if (seenMsgIds.current.has(msg.id)) {
        return;
      }
      seenMsgIds.current.add(msg.id);

      // Play notification sound for incoming messages
      if (!msg.fromMe) {
        try {
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          if (AudioContextClass) {
            const audioCtx = new AudioContextClass();
            const playTone = (freq: number, start: number, duration: number) => {
              const osc = audioCtx.createOscillator();
              const gain = audioCtx.createGain();
              osc.type = "sine";
              osc.frequency.setValueAtTime(freq, audioCtx.currentTime + start);
              gain.gain.setValueAtTime(0.08, audioCtx.currentTime + start);
              gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + start + duration);
              osc.connect(gain);
              gain.connect(audioCtx.destination);
              osc.start(audioCtx.currentTime + start);
              osc.stop(audioCtx.currentTime + start + duration);
            };

            playTone(587.33, 0, 0.15); // D5
            playTone(880, 0.08, 0.2); // A5
          }
        } catch {
          // silent fail
        }
      }

      const current = selectedChatRef.current;

      // Extract the actual raw message data (since it might be inside rawMsg.response)
      const data = (rawMsg && rawMsg.response && typeof rawMsg.response === "object")
        ? (rawMsg.response as Record<string, unknown>)
        : rawMsg;

      const msgPhone = getMessageChatPhone(msg, data);

      // Trigger Desktop Notification for incoming messages when document is not active
      if (!msg.fromMe && (document.hidden || !document.hasFocus())) {
        try {
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification(msg.notifyName || msg.sender?.name || msgPhone || "New WhatsApp Message", {
              body: msg.body || "Sent an attachment",
              icon: "/favicon.ico"
            });
          }
        } catch {
          // silent fail
        }
      }

      if (current) {
        const currentPhone = current.id.user;

        if (msgPhone === currentPhone || (!msgPhone && msg.fromMe)) {
          setMessages((prev) => {
            const exists = prev.some((m) => m.id === msg.id);
            if (exists) return prev;
            return [...prev, msg].sort((a, b) => a.timestamp - b.timestamp);
          });

          // If the message is from the other person (incoming), mark it as seen!
          if (!msg.fromMe && msgPhone === currentPhone && sendSeenRef.current) {
            sendSeenRef.current(msgPhone);
          }
        }
      }

      if (fetchChatsRef.current) {
        fetchChatsRef.current();
      }
    };

    const handleAck = (rawData: Record<string, any>) => {
      let msgId = "";
      if (typeof rawData.id === "string") {
        msgId = rawData.id;
      } else if (rawData.id && typeof rawData.id === "object") {
        msgId = rawData.id._serialized || rawData.id.id || "";
      }

      const ack = rawData.ack as number;
      if (msgId && ack !== undefined) {
        setMessages((prev) =>
          prev.map((m) => (m.id === msgId ? { ...m, ack } : m)),
        );
      }
    };

    const handleChangeState = (rawData: any, sourceEvent: string) => {
      // Detect WhatsApp session disconnect/logout from state changes
      const state = typeof rawData === "string" ? rawData : rawData?.state || rawData?.status || "";
      const isDisconnected =
        /disconnected|logout|logged.?out|session.?closed|unauthorized/i.test(state) ||
        /disconnected|logout|logged.?out|session.?closed|unauthorized/i.test(sourceEvent);

      if (isDisconnected) {
        setTokenInvalid(true);
        return;
      }

      if (fetchChatsRef.current) {
        fetchChatsRef.current();
      }
    };

    socket.on("change_state", (rawData) => handleChangeState(rawData, "change_state"));
    socket.on("change-state", (rawData) => handleChangeState(rawData, "change-state"));

    // Direct disconnect events from WhatsApp Web.js
    socket.on("DISCONNECTED", () => {
      setTokenInvalid(true);
    });

    socket.on("LOGOUT", () => {
      setTokenInvalid(true);
    });

    socket.on("state_change", (rawData: any) => {
      const newState = typeof rawData === "string" ? rawData : rawData?.state || rawData?.status || "";
      if (/disconnected|logout|unauthorized/i.test(newState)) {
        setTokenInvalid(true);
      }
    });

    socket.on("message", (rawMsg) => handleNewMessage(rawMsg, "message"));
    socket.on("message_create", (rawMsg) => handleNewMessage(rawMsg, "message_create"));
    socket.on("received-message", (rawMsg) => handleNewMessage(rawMsg, "received-message"));

    socket.on("message_ack", (rawData) => handleAck(rawData));
    socket.on("onack", (rawData) => handleAck(rawData));

    socket.on("reconnect", () => {
      socket.emit("join", sessionName);
      if (fetchChatsRef.current) {
        fetchChatsRef.current();
      }
    });

    socket.on("reconnect_attempt", () => {
      const freshToken = getTokenFromStorage(sessionName) || token;
      (socket.io as any).opts.extraHeaders = {
        Authorization: `Bearer ${freshToken}`,
      };
      (socket.io as any).opts.auth = {
        token: freshToken,
      };
      (socket.io as any).opts.query = {
        token: freshToken,
      };
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [sessionName, token]);

  // --- Initial data load ---
  useEffect(() => {
    if (sessionName && token) {
      fetchChats();
      fetchArchivedChats();
    }
  }, [sessionName, token, filter, fetchChats, fetchArchivedChats]);

  const [visibleCount, setVisibleCount] = useState(20);

  useEffect(() => {
    setVisibleCount(20);
  }, [filter, searchTerm]);

  const loadedLastMessagesRef = useRef<Map<string, number>>(new Map());

  // Reset cache on session or token change
  useEffect(() => {
    loadedLastMessagesRef.current.clear();
  }, [sessionName, token]);

  const filteredChats = chats.filter((chat) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const name = (
      chat.contact?.name ||
      chat.contact?.pushname ||
      chat.contact?.formattedName ||
      chat.contact?.shortName ||
      chat.groupMetadata?.subject ||
      ""
    ).toLowerCase();
    return name.includes(term);
  });

  const visibleChats = filteredChats.slice(0, visibleCount);
  const hasMoreChats = filteredChats.length > visibleCount;
  const loadMoreChats = useCallback(() => {
    setVisibleCount((prev) => prev + 20);
  }, []);

  // Fetch last messages when visible chats change
  useEffect(() => {
    if (visibleChats.length > 0) {
      const chatsToFetch = visibleChats.filter((c) => {
        const cachedTime = loadedLastMessagesRef.current.get(c.id._serialized);
        return cachedTime === undefined || c.t > cachedTime;
      });

      if (chatsToFetch.length > 0) {
        chatsToFetch.forEach((c) => loadedLastMessagesRef.current.set(c.id._serialized, c.t));
        fetchLastMessages(chatsToFetch);
      }
    }
  }, [visibleChats, fetchLastMessages]);

  const hasToken = !!token;

  return {
    chats: visibleChats,
    archivedChats,
    selectedChat,
    messages,
    loadingChats,
    loadingMessages,
    sendingMessage,
    error,
    filter,
    searchTerm,
    showArchived,
    hasToken,
    tokenInvalid,
    setFilter,
    setSearchTerm,
    setShowArchived,
    selectChat,
    sendSeen,
    sendMessage,
    sendReply,
    sendImage,
    sendVoice,
    sendFile,
    sendLocation,
    sendLink,
    reactToMessage,
    downloadMedia,
    sendAiReply,
    aiReplying,
    fetchChats,
    fetchMessages,
    fetchLastMessages,
    lastMessages,
    hasMoreChats,
    loadMoreChats,
    resolveLidToPhone,
  };
}